/**
 * Shared AGNT investor email-alerts form.
 *
 * Two visual variants driven by the `variant` prop:
 *   - `bar`   — compact horizontal row (First / Last / Email / Submit), used by
 *               the sitewide `InvestorAlertsBar` above the footer.
 *   - `panel` — stacked layout (email + inline Submit on row 1, First/Last on
 *               row 2), used in the home page Calendar/Alerts section.
 *
 * Submits to the `/api/agnt/investor-alerts` endpoint and surfaces success /
 * error feedback inline.
 */

import { type ChangeEvent, type FormEvent, useState } from 'react';

import styles from './investor-alerts-form.module.scss';

export type InvestorAlertsFormVariant = 'bar' | 'panel';

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface ApiResult {
  ok: boolean;
  message: string;
}

export default function InvestorAlertsForm({ variant }: { variant: InvestorAlertsFormVariant }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const res = await fetch('/api/agnt/investor-alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName, email }),
      });
      const result: ApiResult = await res.json();
      if (result.ok) {
        setStatus('success');
        setFeedback(result.message);
        setFirstName('');
        setLastName('');
        setEmail('');
      } else {
        setStatus('error');
        setFeedback(result.message);
      }
    } catch {
      setStatus('error');
      setFeedback('Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return <p className={styles.feedbackSuccess}>{feedback}</p>;
  }

  const submitting = status === 'submitting';

  if (variant === 'bar') {
    return (
      <>
        <form className={styles.barForm} onSubmit={handleSubmit}>
          <input
            className={styles.barInput}
            type="text"
            placeholder="First Name"
            aria-label="First Name"
            value={firstName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
            required
            disabled={submitting}
          />
          <input
            className={styles.barInput}
            type="text"
            placeholder="Last Name"
            aria-label="Last Name"
            value={lastName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
            required
            disabled={submitting}
          />
          <input
            className={styles.barInput}
            type="email"
            placeholder="Email"
            aria-label="Email"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            required
            disabled={submitting}
          />
          <button className={styles.barSubmit} type="submit" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </form>
        {status === 'error' && <p className={styles.feedbackError}>{feedback}</p>}
      </>
    );
  }

  return (
    <>
      <form className={styles.panelForm} onSubmit={handleSubmit}>
        <label className={styles.panelLabel} htmlFor="agnt-alert-email">Email Address</label>
        <div className={styles.panelEmailRow}>
          <input
            id="agnt-alert-email"
            className={styles.panelInput}
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            required
            disabled={submitting}
          />
          <button className={styles.panelSubmit} type="submit" disabled={submitting}>
            {submitting ? '…' : 'Subscribe'}
          </button>
        </div>
        <div className={styles.panelNameRow}>
          <input
            className={styles.panelInput}
            type="text"
            placeholder="First Name"
            aria-label="First Name"
            value={firstName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
            required
            disabled={submitting}
          />
          <input
            className={styles.panelInput}
            type="text"
            placeholder="Last Name"
            aria-label="Last Name"
            value={lastName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
            required
            disabled={submitting}
          />
        </div>
      </form>
      {status === 'error' && <p className={styles.feedbackError}>{feedback}</p>}
    </>
  );
}
